import React from 'react';
import _ from 'underscore';
import { FeatureFlags } from '~src/ts/FeatureFlags';
import { ButtonProps, ButtonType, DialogFooterButtonsWidget, DialogType } from '@tableau/widgets';
import { DataGrid, GridSelection } from '@tableau/widgets-datagrid';
import { Messages } from '../../codegen/Localize';
import { CommonStyles } from '../../common/react/CommonStyles';
import { ModalInstance, ReactModalService } from '../../commonInfra/dialogs/ReactModalService';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { AuthorizableType } from '../../permissions/types/AuthorizableType';
import { Capabilities } from '../../permissions/types/Capabilities';
import { CapabilitySection } from '../../permissions/types/CapabilitySections';
import { PermissionRoleName } from '../../permissions/types/PermissionRole';
import { PermissionRoleHelper } from '../../permissions/utils/PermissionRoleHelper';
import { ListResult } from '../../ts/fetchers/ListResult';
import { deepClone } from '../../ts/helpers/DeepClone';
import { ISiteUserInfo } from '../../ts/models/ISiteUserInfo';
import { IGroup } from '../../ts/ServerApi/types/IGroup';
import { IUser } from '../../ts/ServerApi/types/IUser';
import { PermissionsGridStyles } from '../styles/PermissionsGridStyle';
import { IGranteePermissions } from '../types/IGranteePermissions';
import { IPermission, Permission, PermissionReason } from '../types/Permission';
import { PermissionsHelper } from '../utils/PermissionsHelper';
import { AddNewPermission } from './AddNewPermission';
import { PermissionsGridCells } from './PermissionsGridCells';
import { PermissionsGridEmptyState } from './PermissionsGridEmptyState';

export enum RowStatus {
  INITIAL,
  SELECTED,
  EDIT_CLEAN,
  EDIT_DIRTY,
  EDIT_DELETE,
  DISABLED,
  SELECTED_SEARCH,
  RELEVANT,
}

export enum Action {
  SET_PL,
  DELETE,
}

export type PermissionsGridRow = {
  granteeFullyQualifiedName?: string;
  granteeName?: string;
  granteeId?: string;
  index: number;
  isInheritedProjectLeader: boolean;
  isProjectLeader: boolean;
  isUser?: boolean;
  permissionRole: PermissionRoleName;
  permissionRoleOptions: PermissionRoleName[];
  permissions: _.Dictionary<IPermission>;
  siteRole: string;
  status: RowStatus;
};

export const ACTION_DIALOG_TESTID = 'permissions-action-dialog';
export const PL_SAVE_BUTTON_TESTID = 'permissions-pl-dialog-save';
export const PERMISSIONS_CONTENT_TESTID = 'permissions-content';
export const CONFIRM_DELETE_BUTTON_TESTID = 'permissions-delete-dialog-confirm-delete';
export const FIRST_ROW_INDEX = 0;

export type PermissionsGridProps = {
  /** Permissions to display as rows of this grid */
  permissions: ListResult<IGranteePermissions>;

  /** Helper class for calculating permission role */
  permissionRoleHelper: PermissionRoleHelper;

  /** The sections of capabilities to group together */
  capabilitySections: ReadonlyArray<CapabilitySection>;

  /** Authorizable type that the grid is rendered for */
  authorizableType: AuthorizableType;

  /** Container authorizable type that the dialog is opened for (which is not necessarily the same as authorizableType) */
  containerAuthorizableType?: AuthorizableType;

  isExplicitPermissions: boolean;

  isSessionSiteOrServerAdmin: boolean;

  /** Whether permission rules are editable */
  isEditable?: boolean;

  /** Description for what the permissions editability means */
  editabilityDescription?: string;

  /** Unspecified permissions used to initialize a new rule */
  unspecifiedPermissions?: _.Dictionary<_.Dictionary<IPermission>>;

  /** Callback triggered when a new row is selected */
  onRowSelect: (rowData: PermissionsGridRow, selectedFromEffectivePermissions: boolean, selectedRelevantRule: boolean) => void;

  /** Save edits to permissions */
  savePermissionEdits?: (permissions: IGranteePermissions) => Promise<void | IResult>;

  /** Function to call whenever edit mode updates */
  onEditModeChange?: (isEditingPermissionRule: boolean) => void;

  /** Whether rows not in edit mode should show as disabled */
  disableNonEditingRows: boolean;

  /** Whether the selected rule was selected via search or click */
  selectedFromSearch: boolean;

  getNextPermissionsPage: () => void;

  getSearchTextBoxText: () => string;

  clearSearchTextBoxText: () => void;
};

export type PermissionsGridState = {
  /** Permission rule that has been copied: keyed by content type, then capability */
  copiedPermissions?: _.Dictionary<_.Dictionary<IPermission>>;

  /** Index of the row currently in edit mode, only used for Explicit Permissions Grid */
  editModeRowIndex?: number;

  /** Index of the row currently selected, only used for Explicit Permissions Grid */
  selectedRowIndex?: number;

  /** Unsaved permission rule of the row in edit mode: keyed by content type, then capability */
  editModeRowPermissions?: _.Dictionary<_.Dictionary<IPermission>>;

  /** Grantee permissions if adding a new row */
  newRowGranteePermissions?: IGranteePermissions;

  /** Grantee permissions that is most recently saved, used to highlight and scroll to the corresponding row after re-rendering */
  lastSavedGranteePermissions?: IGranteePermissions;

  /** If waiting for a response from server */
  loading?: boolean;

  /** User id of relevant permission rules */
  relevantUserId?: string;

  /** Mapping of user id to group ids, used to compute group ids of relevant permission rules */
  relevantGroupMembership?: _.Dictionary<string[]>;

};

export class PermissionsGrid extends React.Component<PermissionsGridProps, PermissionsGridState> {
  public state: PermissionsGridState = {
  };

  /** Used by DataGrid's rowStore; public for testing purposes */
  public rows: DataGrid.Row[] = [];

  /** Passed into DataGrid's prop.cols */
  private cols: DataGrid.Column[] = [];

  private dataGridScrollHandler: DataGrid.IScrollable;

  /** Temporarily saved scroll position to be restored after rerendering - TFSID 1043824 */
  private scrollPositionToRestore: number = undefined;

  public componentDidUpdate(prevProps: PermissionsGridProps, prevState: PermissionsGridState) {
    // do nothing when permissions have not been populated
    if (!prevProps.permissions || !this.props.permissions) return;

    // logic for Explicit Permissions Grid - start
    if (this.props.isExplicitPermissions) {
      if (this.scrollPositionToRestore !== undefined) {
        this.restoreScrollPosition();
      }

      const rowWasAdded = this.props.permissions.total > prevProps.permissions.total;
      if (rowWasAdded) {
        // need to select the new row
        this.highlightAndScrollToAddedRow();
        return;
      }

      // whenever a row is selected, we need to handle that selection by calling props.onRowSelect
      // the logic here in componentDidUpdate figures out when there has been a row selection

      // Case 0) Selecting a grantee for a new row being added.
      const newGranteeWasSelected = prevState.newRowGranteePermissions !== undefined
        && (prevState.newRowGranteePermissions.group === undefined && prevState.newRowGranteePermissions.user === undefined)
        && this.state.newRowGranteePermissions !== undefined
        && (this.state.newRowGranteePermissions.group !== undefined || this.state.newRowGranteePermissions.user !== undefined);
      if (newGranteeWasSelected) {
        this.handleRowSelect(this.state.selectedRowIndex);
        return;
      }

      // Case 0.5) Cancel selecting a grantee for a new row being added, but there are no existing explicit permissions so select undefined rule

      // Prereqs to consider a handling a row for Case 1 & 2: a row is selected, we are not in edit mode, and the row was not selected from search.
      // The cases we need to handle are clicking a different row and canceling edit mode on a row (which in turn covers many cases).

      // Case 1) selectedRowIndex changing represents clicking a different row, saving a newly added row,
      //         deleting a row above the selected row, selecting the last row and deleting it, and selecting All Users when the grid first loads.
      const selectedIndexHasChanged = prevState.selectedRowIndex !== this.state.selectedRowIndex;
      // Case 2) Canceling edit mode also happens when an edit is saved, the selected row is deleted, or a change is made to Project Leader status,
      //         and in these cases the selected index doesn't changed, but the permissions (or grantee) at that index do change.
      const selectedRowPermissionsHaveChanged = prevState.selectedRowIndex !== undefined && !_.isEqual(prevProps.permissions.items[prevState.selectedRowIndex], this.props.permissions.items[this.state.selectedRowIndex]);

      if (
        (this.state.selectedRowIndex !== undefined || (this.state.selectedRowIndex === undefined && this.props.permissions?.total === 0)) &&
        this.state.editModeRowIndex === undefined &&
        !this.props.getSearchTextBoxText() &&
        (selectedIndexHasChanged || selectedRowPermissionsHaveChanged)
      ) {
        const rowWasDeleted = prevProps.permissions.total > this.props.permissions.total;
        const lastRowWasSelected = this.state.selectedRowIndex > this.props.permissions.items.length - 1;
        if (lastRowWasSelected && rowWasDeleted) {
          // need to decrement the selected index by 1 to select the new last row
          let nextIndexToSelect = this.props.permissions.items.length - 1;
          // but when collectionsPermissionsEnabled is on, the grid can become empty after deletion;
          // in that case need to clear out the selected row index.
          if (FeatureFlags.collectionsPermissionsEnabled && !this.props.permissions.items.length) {
            nextIndexToSelect = undefined;
          }
          this.setState({ selectedRowIndex: nextIndexToSelect });
          this.handleRowSelect(nextIndexToSelect);
        } else {
          // for all other cases, the currently selected row has already been correctly updated, so handle its selection
          this.handleRowSelect(this.state.selectedRowIndex);
        }
      }
    }
    // logic for Explicit Permissions Grid - end

    // logic for Effective Permissions Grid - start
    if (!this.props.isExplicitPermissions) {
      // when the selected row has changed, handle the selection
      if (prevState.selectedRowIndex !== this.state.selectedRowIndex) {
        this.handleRowSelect(this.state.selectedRowIndex);
      }
      // when the grid is updated to show as disabled or show different permissions, clear row selection
      const showAsDisabled = this.props.disableNonEditingRows && !prevProps.disableNonEditingRows;
      const showDifferentPermissions = !_.isEqual(prevProps.permissions, this.props.permissions);
      if (showAsDisabled || showDifferentPermissions) {
        this.clearSelectedRow();
      }
    }
    // logic for Effective Permissions Grid - end
  }

  public setRelevantUserId = (userId: string): void => {
    this.setState((prevState) => {
      return prevState.relevantUserId !== userId ? { relevantUserId: userId } : null;
    });
  }

  public setRelevantGroupMembership = (updatedGroupMembership: _.Dictionary<string[]>, appendResults?: boolean): void => {
    this.setState((prevState) => {
      if (appendResults) {
        if (updatedGroupMembership) {
          const combinedGroupMembership = { ...prevState.relevantGroupMembership, ...updatedGroupMembership };
          return !_.isEqual(prevState.relevantGroupMembership, combinedGroupMembership) ? { relevantGroupMembership: combinedGroupMembership } : null;
        }
        return null;
      }
      return !_.isEqual(prevState.relevantGroupMembership, updatedGroupMembership) ? { relevantGroupMembership: updatedGroupMembership } : null;
    });
  }

  /** Selects the passed row index. By default, selects the first row. */
  public setSelectedRow = (rowIndex = FIRST_ROW_INDEX) => {
    if (this.props.isExplicitPermissions) {
      let isSelectionValid: boolean = true;

      // when collectionsPermissionsEnabled is on, the grid can be empty,
      // so make sure the passed row index can actually be selected.
      if (FeatureFlags.collectionsPermissionsEnabled) {
        isSelectionValid = this.props.permissions.items.length > rowIndex;
      }

      if (isSelectionValid) {
        this.setState((prevState) => {
          if (prevState.selectedRowIndex === rowIndex) {
            // don't re-render if the row is already selected
            return null;
          } else {
            this.scrollIntoView(rowIndex);
            return { selectedRowIndex: rowIndex, };
          }
        });
      } else {
        // no selection is a valid case: upon cancelling adding a new rule when there are no existing explicit permissions,
        // the selected row should be set to undefined so both grids can be cleared
        this.setState({selectedRowIndex: undefined});
      }
    }
  }

  /** Clears the selected row */
  public clearSelectedRow = () => {
    this.setState((prevState) => {
      if (prevState.selectedRowIndex === undefined) {
        return null;
      } else {
        return { selectedRowIndex: undefined, };
      }
    });
  }

  private handleRowSelect = (index: number, selectedRelevantRule: boolean = false): void => {
    const rowData = typeof index === 'number' ? this.rows[index].data as PermissionsGridRow : undefined;
    const selectedFromEffectivePermissions = !this.props.isExplicitPermissions;
    this.props.onRowSelect(rowData, selectedFromEffectivePermissions, selectedRelevantRule);
  }

  private handleCellClick = (gridRow: PermissionsGridRow): void => {

    if (this.props.isExplicitPermissions) {
      // Clear the search text if user explicitly clicks in explicit grid
      this.props.clearSearchTextBoxText();
    }

    if (this.shouldShowRelevantRows()) {
      // clicking when showing relevant permission rules overrides normal clicking
      this.handleRowSelect(/* index */ undefined, /* selectedRelevantRule */ true);
    } else if (!this.props.disableNonEditingRows) {

      if (gridRow.index !== this.state.selectedRowIndex) {
        this.setState({ selectedRowIndex: gridRow.index });
      } else if (!this.props.isExplicitPermissions) {
        // when clicking the same row again in the effective permissions grid, should clear row selection
        this.setState({ selectedRowIndex: undefined });
      } else if (this.props.selectedFromSearch && gridRow.index === FIRST_ROW_INDEX) {
        this.handleRowSelect(FIRST_ROW_INDEX);
      }
    }
  }

  private handleCellDoubleClick = (rowData: PermissionsGridRow): void => {
    !(rowData.isProjectLeader || rowData.isInheritedProjectLeader) && this.openEditMode(rowData.index, this.props.permissions.items[rowData.index].permissionsByContentType);
  }

  private highlightAndScrollToAddedRow = (): void => {
    this.setState((prevState) => {
      if (prevState.lastSavedGranteePermissions) {
        const existingRowIndex = this.getExistingRowIndex(prevState.lastSavedGranteePermissions.group, prevState.lastSavedGranteePermissions.user);
        if (existingRowIndex !== undefined) {
          this.scrollIntoView(existingRowIndex);
          return {
            selectedRowIndex: existingRowIndex,
            lastSavedGranteePermissions: undefined,
          };
        } else {
          return null;
        }
      } else {
        return null;
      }
    });
  }

  private restoreScrollPosition = (): void => {
    // TFSID 1168294: With recent data grid changes, scrolling is blocked while paging in new cells,
    // so adding setTimeout around the scrolling action to run it in the next rendering cycle.
    setTimeout(() => {
      this.scrollIntoView(this.state.selectedRowIndex, this.scrollPositionToRestore);
      this.scrollPositionToRestore = undefined;
    });
  }

  private saveScrollPosition = (): void => {
    this.scrollPositionToRestore = this.dataGridScrollHandler.getScrollPosition().scrollTop;
  }

  private scrollIntoView = (index: number, scrollTopOverride?: number): void => {
    const scrollPosition = scrollTopOverride !== undefined ? scrollTopOverride : this.dataGridScrollHandler.getScrollPosition().scrollTop;
    const gridHeight = PermissionsGridStyles.MaxRows * PermissionsGridStyles.RowHeight;
    const firstVisibleIndex = Math.ceil(scrollPosition / PermissionsGridStyles.RowHeight);
    const lastVisibleIndex = Math.floor((scrollPosition + gridHeight) / PermissionsGridStyles.RowHeight) - 1;
    if (index + 1 > lastVisibleIndex) {
      // if the edit button panel wouldn't be visible, scroll so that the current index is the second from bottom row
      this.dataGridScrollHandler.setScrollPosition(0, index * PermissionsGridStyles.RowHeight - (gridHeight - 2 * PermissionsGridStyles.RowHeight));
    } else if (index < firstVisibleIndex) {
      this.dataGridScrollHandler.setScrollPosition(0, index * PermissionsGridStyles.RowHeight);
    } else if (scrollTopOverride !== undefined) {
      this.dataGridScrollHandler.setScrollPosition(0, scrollTopOverride);
    }
  }

  private canOpenEditMode = (): boolean => {
    return this.props.isEditable && this.state.editModeRowIndex === undefined;
  }

  private openEditMode = (index: number, permissions: _.Dictionary<_.Dictionary<IPermission>>, isNewRow?: boolean): void => {
    if (this.canOpenEditMode()) {
      this.saveScrollPosition();
      this.scrollIntoView(index);
      const previouslySelected = this.state.selectedRowIndex === index;
      this.setState({
        selectedRowIndex: index,
        editModeRowIndex: index,
        editModeRowPermissions: permissions,
        newRowGranteePermissions: isNewRow ? { permissionsByContentType: permissions } : undefined,
      }, () => {
        if (!isNewRow && !previouslySelected) {
          // opening new row in edit mode doesn't count as selecting a new row
          // double clicking a row to open edit mode also shouldn't handle row selection, as it would be already handled by clicking the row (part of double click)
          // however we need to select it otherwise so that if edit mode was entered not via clicking the row shows the correct effective permissions
          this.handleRowSelect(index);
        }
      });
      this.props.onEditModeChange && this.props.onEditModeChange(true);
    }
  }

  private cancelEditMode = (newSelectedRowIndex?: number, lastSavedGranteePermissions?: IGranteePermissions): void => {
    this.setState((prevState) => {
      if (prevState.editModeRowIndex === undefined) {
        return null;
      }
      if (lastSavedGranteePermissions === undefined && typeof newSelectedRowIndex !== 'number' && prevState.newRowGranteePermissions !== undefined) {
        // handle canceling adding a new row by closing edit mode and selecting all users
        this.setSelectedRow();
        return {
          editModeRowIndex: undefined,
          editModeRowPermissions: undefined,
          newRowGranteePermissions: undefined,
          lastSavedGranteePermissions: undefined,
        };
      }
      let selectedRowIndex: number;
      if (typeof newSelectedRowIndex === 'number') {
        // canceling edit mode because attempted to add a row that already existed, so select that row
        selectedRowIndex = newSelectedRowIndex;
      } else {
        if (prevState.newRowGranteePermissions !== undefined) {
          selectedRowIndex = undefined;
        } else {
          selectedRowIndex = prevState.selectedRowIndex;
        }
      }
      return {
        selectedRowIndex,
        editModeRowIndex: undefined,
        editModeRowPermissions: undefined,
        newRowGranteePermissions: undefined,
        lastSavedGranteePermissions,
      };
    });
    this.props.onEditModeChange && this.props.onEditModeChange(false);
  }

  private openActionDialog = (title: string, message: string, confirmButton: ButtonProps, confirmButtonAction: () => void): void => {
    let modalInstance: ModalInstance<'cancel' | 'submit'>;

    const onCancel = (): void => {
      modalInstance && modalInstance.close('cancel');
    };

    confirmButton.handleClick = () => { modalInstance && modalInstance.close('submit'); confirmButtonAction(); };

    const dialogFooter: JSX.Element = (
      <DialogFooterButtonsWidget
        buttons={[
          {
            handleClick: onCancel,
            buttonType: ButtonType.Outline,
            children: Messages.cancel(),
            testId: `${ACTION_DIALOG_TESTID}-cancel`,
          },
          confirmButton,
        ]}
        testId={`${ACTION_DIALOG_TESTID}-footer`}
      />
    );

    const dialogContent: JSX.Element = (
      <span className={PermissionsGridStyles.ActionDialogText}>{message}</span>
    );

    modalInstance = ReactModalService.open({
      dialogContent,
      dialogWidth: 375,
      footerContent: dialogFooter,
      title,
      testId: ACTION_DIALOG_TESTID,
      type: DialogType.NormalWithTitleBar,
    });
  }

  private handleUpdateProjectLeader = (index: number, originalProjectLeaderPermission: Permission): void => {

    switch (originalProjectLeaderPermission) {
      case Permission.Allowed:
        // Allowed -> Unspecified, open edit mode with all caps (including PL) as unspecified
        let unspecifiedPermissions = deepClone(this.props.unspecifiedPermissions);
        this.openEditMode(index, unspecifiedPermissions);
        break;
      case Permission.Denied:
        // changing from Denied -> Unspecified, just send request with no extra UI
        let originalPermissions: IGranteePermissions = this.props.permissions.items[index];
        this.savePermissions(originalPermissions, this.getProjectLeaderEdit(Permission.Unspecified));
        break;
      case Permission.Unspecified:
        // changing from Unspecified -> Allowed, but first pop confirmation dialog
        originalPermissions = this.isNewRowInEditMode() ? this.state.newRowGranteePermissions : this.props.permissions.items[index];
        const confirmButton: ButtonProps = {
          buttonType: ButtonType.Go,
          children: Messages.permissionsSetProjectLeader(),
          testId: PL_SAVE_BUTTON_TESTID,
        };
        const handleSaveProjectLeader = (): void => {
          this.savePermissions(originalPermissions, this.getProjectLeaderEdit(Permission.Allowed));
        };
        this.openActionDialog(Messages.permissionsSetProjectLeader(), Messages.permissionsProjectLeaderDescription(), confirmButton, handleSaveProjectLeader);
        break;
    }
  }

  private getProjectLeaderEdit = (permission: Permission): _.Dictionary<_.Dictionary<IPermission>> => {
    return {
      [AuthorizableType.PROJECT]: {
        [Capabilities.projectLeader]: {
          permission,
          reason: PermissionReason.Unspecified,
        },
      },
    };
  }

  private getDeleteWarningMessage = (authType: AuthorizableType, granteeType: string): string => {
    const messageMapping: Partial<Record<AuthorizableType, (a: { GROUP_OR_USER: string }) => string>> = {
      [AuthorizableType.COLLECTION]: Messages.permissionsDeletePermissionRuleWarning_collection,
      [AuthorizableType.DATABASE]: Messages.permissionsDeletePermissionRuleWarning_database,
      [AuthorizableType.DATAROLE]: Messages.permissionsDeletePermissionRuleWarning_dataRole,
      [AuthorizableType.LENS]: Messages.permissionsDeletePermissionRuleWarning_lens,
      [AuthorizableType.DATASOURCE]: Messages.permissionsDeletePermissionRuleWarning_dataSource,
      [AuthorizableType.FLOW]: Messages.permissionsDeletePermissionRuleWarning_flow,
      [AuthorizableType.METRIC]: Messages.permissionsDeletePermissionRuleWarning_metric,
      [AuthorizableType.PROJECT]: Messages.permissionsDeletePermissionRuleWarning_project,
      [AuthorizableType.PUBLISHED_CONNECTION]: Messages.permissionsDeletePermissionRuleWarning_publishedConnection,
      [AuthorizableType.TABLE]: Messages.permissionsDeletePermissionRuleWarning_table,
      [AuthorizableType.VIEW]: Messages.permissionsDeletePermissionRuleWarning_view,
      [AuthorizableType.WORKBOOK]: Messages.permissionsDeletePermissionRuleWarning_workbook,
    };
    return messageMapping[authType]({ GROUP_OR_USER: granteeType });
  }

  private handleDeleteRule = (index: number): void => {
    const originalPermissions: IGranteePermissions = this.props.permissions.items[index];
    const granteeType: string = originalPermissions.user ? Messages.permissionsGranteeType_user() : Messages.permissionsGranteeType_group();
    const dialogAuthType: AuthorizableType = this.props.containerAuthorizableType ? this.props.containerAuthorizableType : this.props.authorizableType;
    const deleteWarningMessage: string = this.getDeleteWarningMessage(dialogAuthType, granteeType);
    const confirmButton: ButtonProps = {
      buttonType: ButtonType.Destructive,
      children: Messages.deleteMessage(),
      testId: CONFIRM_DELETE_BUTTON_TESTID,
    };
    const handleDelete = (): void => {
      let unspecifiedPermissions = deepClone(this.props.unspecifiedPermissions);
      this.savePermissions(originalPermissions, unspecifiedPermissions).then(() => {
        let indexToSelect: number = this.state.selectedRowIndex;
        if (indexToSelect > index) {
          // if we are deleting a row that is above the selected row, keep the selected row highlighted by decreasing selected row index by 1.
          indexToSelect--;
        } else if (this.props.permissions.total > 1 && indexToSelect === index && index >= this.props.permissions.total - 1) {
          // if the last row is selected and deleted, select the previous row (the new last row),
          // as long as there is more than 1 existing rule: if there is only 1 permission, it is the all users rule which can't get deleted
          indexToSelect--;
        }
        this.setState({ selectedRowIndex: indexToSelect });
      });
    };
    this.openActionDialog(Messages.permissionsDeletePermissionRule(), deleteWarningMessage, confirmButton, handleDelete);
  }

  private savePermissions = (originalPermissions: IGranteePermissions, newPermissionsByContentType: _.Dictionary<_.Dictionary<IPermission>>): Promise<void | IResult> => {
    let newGranteePermissions: IGranteePermissions = deepClone(originalPermissions);
    newGranteePermissions.permissionsByContentType = newPermissionsByContentType;
    return this.props.savePermissionEdits(newGranteePermissions)
      .catch(() => {
        this.setState({ loading: false, });
      })
      .then(() => {
        this.setState({ loading: false, });
        this.cancelEditMode(undefined, newGranteePermissions);
      });
  }

  private savePermissionEdits = (): void => {
    const newRowAdded: boolean = this.isNewRowInEditMode();
    const editRowIndex = this.state.editModeRowIndex;
    const originalPermissions: IGranteePermissions = newRowAdded ? this.state.newRowGranteePermissions : this.props.permissions.items[editRowIndex];
    let permissionEdits: _.Dictionary<_.Dictionary<IPermission>> = {};

    // send only changed, non-mixed permissions to server
    _.each(this.state.editModeRowPermissions, (permissions: _.Dictionary<IPermission>, contentType: string) => {
      permissionEdits[contentType] = {};
      _.each(permissions, (permission: IPermission, capability: string) => {
        if (permission.permission !== Permission.Mixed && permission.permission !== originalPermissions.permissionsByContentType[contentType][capability].permission) {
          permissionEdits[contentType][capability] = permission;
        }
      });
    });
    this.setState({ loading: true, });
    this.savePermissions(originalPermissions, permissionEdits);
  }

  private getRowStore = (): DataGrid.RowStore => {
    return {
      getItems: (start: number, end: number, rowHandler: (rows: DataGrid.Row[]) => void) => {
        rowHandler(this.rows);
        if (!this.props.isExplicitPermissions && this.props.permissions && this.props.permissions.total > this.props.permissions.items.length && end + 50 >= this.props.permissions.items.length) {
          this.props.getNextPermissionsPage();
        }
      }
    } as DataGrid.RowStore;
  }

  private selectionHandler = (): GridSelection => {
    // rendering for relevant permission rules overrides normal rendering, e.g. selected row
    return this.shouldShowRelevantRows() ? null : {
      isSelected: (cell: DataGrid.Cell): boolean => {
        return this.isRowSelected(cell.rowIdx);
      }
    };
  }

  private shouldShowRelevantRows = (): boolean => {
    return this.state.relevantUserId !== undefined && this.state.relevantGroupMembership !== undefined;
  }

  private isRowRelevant = (index: number): boolean => {
    const permission = this.props.permissions.items[index];
    if (permission) {
      if (permission.user) {
        return permission.user.id === this.state.relevantUserId;
      } else {
        const relevantGroupIds = this.state.relevantGroupMembership[this.state.relevantUserId];
        return relevantGroupIds && relevantGroupIds.includes(permission.group.id);
      }
    } else {
      return false;
    }
  }

  private isNewRowInEditMode = (): boolean => {
    return this.state.newRowGranteePermissions !== undefined;
  }

  private isRowInEditMode = (index: number): boolean => {
    return index === this.state.editModeRowIndex;
  }

  private isRowDisabled = (index: number): boolean => {
    return this.props.disableNonEditingRows ? index !== this.state.editModeRowIndex : false;
  }

  private isRowSelected = (index: number): boolean => {
    return index === this.state.selectedRowIndex;
  }

  private handleCopyPermissionsRow = (index: number): void => {
    this.setState({ copiedPermissions: this.props.permissions.items[index].permissionsByContentType });
  }

  private handlePastePermissionsRow = (index: number): void => {
    if (this.canOpenEditMode()) {
      this.scrollIntoView(index);
      this.setState((prevState) => {
        return {
          selectedRowIndex: index,
          editModeRowIndex: index,
          editModeRowPermissions: prevState.copiedPermissions,
        };
      }, () => this.handleRowSelect(index));
      this.props.onEditModeChange && this.props.onEditModeChange(true);
    } else if (this.isRowInEditMode(index)) {
      this.setState((prevState) => {
        return {
          editModeRowPermissions: prevState.copiedPermissions,
        };
      });
    }
  }

  private isRowEdited = (index: number): boolean => {
    if (this.isNewRowInEditMode()) {
      // also check a grantee has been selected if adding a new row
      return (this.state.newRowGranteePermissions.user || this.state.newRowGranteePermissions.group) &&
        !_.isEqual(this.state.editModeRowPermissions, this.state.newRowGranteePermissions.permissionsByContentType);
    } else {
      return this.isRowInEditMode(index) && !_.isEqual(this.state.editModeRowPermissions, this.props.permissions.items[index].permissionsByContentType);
    }
  }

  private isRowToBeDeleted = (index: number): boolean => {
    // project leader is not in a role template, so has to be checked separately
    // impossible for inherited project leader to be unspecified or denied, so no need to check inherited project leader capability
    let isProjectLeaderUnspecified = true;
    if (
      this.state.editModeRowPermissions[AuthorizableType.PROJECT] &&
      this.state.editModeRowPermissions[AuthorizableType.PROJECT][Capabilities.projectLeader] &&
      this.state.editModeRowPermissions[AuthorizableType.PROJECT][Capabilities.projectLeader].permission !== Permission.Unspecified
    ) {
      isProjectLeaderUnspecified = false;
    }

    // can't delete a row that hasn't been added yet (can't delete a new row)
    return isProjectLeaderUnspecified && !this.isNewRowInEditMode() && this.isRowInEditMode(index) && _.every(this.state.editModeRowPermissions, (permissions: _.Dictionary<IPermission>, contentType: string) => {
      const currentPermissionRole = this.props.permissionRoleHelper.getExplicitRoleForTypeAndPermissions(
        contentType,
        { permissionsByContentType: this.state.editModeRowPermissions },
      );
      return currentPermissionRole === PermissionRoleName.None;
    });
  }

  private isHeaderDisabled = (): boolean => {
    // Only effective permissions grid can show header row in disabled style
    return !this.props.isExplicitPermissions && this.props.disableNonEditingRows;
  }

  private getExistingRowIndex = (groupToMatch?: IGroup, userToMatch?: IUser): number | undefined => {
    let existingRowIndex: number = undefined;
    this.props.permissions && this.props.permissions.items.find((row, index) => {
      if ((groupToMatch && row.group && row.group.id === groupToMatch.id.toString()) ||
        (userToMatch && row.user && row.user.id === userToMatch.id.toString())) {
        existingRowIndex = index;
        return true;
      }
      return false;
    });
    return existingRowIndex;
  }

  private handleNewRowGranteeSelect = (selectedGroup?: IGroup, selectedUser?: IUser): void => {
    // When the selected grantee already has a row in the grid, switch edit mode to be for that existing row.
    // But if the selected grantee is a PL or IPL, then just cancel edit mode because PL or IPL doesn't allow edit mode.
    const existingRowIndex = this.getExistingRowIndex(selectedGroup, selectedUser);
    if (existingRowIndex !== undefined) {
      const rowData = this.rows[existingRowIndex].data as PermissionsGridRow;
      const isPLorIPL: boolean = rowData.isProjectLeader || rowData.isInheritedProjectLeader;
      this.scrollIntoView(existingRowIndex);
      isPLorIPL ? this.cancelEditMode(existingRowIndex) : this.setState({
        selectedRowIndex: existingRowIndex,
        editModeRowIndex: existingRowIndex,
        editModeRowPermissions: this.props.permissions.items[existingRowIndex].permissionsByContentType,
        newRowGranteePermissions: undefined,
      }, () => this.handleRowSelect(existingRowIndex));
    } else {
      this.setState((prevState) => {
        return {
          newRowGranteePermissions: { group: selectedGroup, user: selectedUser, permissionsByContentType: prevState.newRowGranteePermissions.permissionsByContentType }
        };
      });
    }
  }

  private getGranteeColumn = (): DataGrid.Column => {
    let cellFormatter;
    const rowToGranteeData = (row: DataGrid.Row): PermissionsGridCells.IGrantee => {
      const permissionsData = row.data as PermissionsGridRow;
      const isRowInEditMode = this.isRowInEditMode(permissionsData.index);
      return {
        fullyQualifiedName: permissionsData.granteeFullyQualifiedName,
        name: permissionsData.granteeName,
        isProjectLeader: permissionsData.isProjectLeader,
        isInheritedProjectLeader: permissionsData.isInheritedProjectLeader && !isRowInEditMode,
        isUser: permissionsData.isUser,
        rowStatus: permissionsData.status,
      };
    };

    if (this.props.isEditable) {
      const disabledPaste = this.state.copiedPermissions === undefined;
      const disabledDelete = this.isNewRowInEditMode();
      const isLoading = this.state.loading;
      cellFormatter = PermissionsGridCells.EditableGranteeCell(
        this.cancelEditMode,
        this.savePermissionEdits,
        this.handleCopyPermissionsRow,
        this.handlePastePermissionsRow,
        this.handleUpdateProjectLeader,
        this.handleNewRowGranteeSelect,
        this.handleDeleteRule,
        disabledPaste,
        rowToGranteeData,
        this.props.authorizableType,
        disabledDelete,
        isLoading
      );
    } else {
      cellFormatter = PermissionsGridCells.GranteeCell(this.props.isExplicitPermissions, rowToGranteeData);
    }
    return {
      name: 'grantee',
      dataKey: 'granteeName',
      headerText: this.props.isExplicitPermissions ? Messages.groupsOrUsers() : Messages.user(),
      resizable: false,
      sortable: false,
      preferredWidth: PermissionsGridStyles.GranteeCellWidth,
      headerFormatter: PermissionsGridCells.TextHeaderCell(this.isHeaderDisabled()),
      cellFormatter,
    };
  }

  private getPermissionsWithPermissionRoleChange = (basePermissions: _.Dictionary<_.Dictionary<IPermission>>, newValue: string): _.Dictionary<_.Dictionary<IPermission>> => {
    // Make a copy of basePermissions and apply changes based on new role
    let permissionsWithPermissionRoleChange: _.Dictionary<_.Dictionary<IPermission>> = deepClone(basePermissions);
    permissionsWithPermissionRoleChange[this.props.authorizableType] = PermissionsHelper.IPermissionsFromPermissions(
      this.props.permissionRoleHelper.getPermissionsForTypeAndRole(this.props.authorizableType, newValue)
    );

    // project leader is not included in Project role template, so need to preserve original value even when role has been changed
    if (basePermissions[AuthorizableType.PROJECT] && basePermissions[AuthorizableType.PROJECT][Capabilities.projectLeader]) {
      permissionsWithPermissionRoleChange[AuthorizableType.PROJECT][Capabilities.projectLeader] = deepClone(basePermissions[AuthorizableType.PROJECT][Capabilities.projectLeader]);
    }
    return permissionsWithPermissionRoleChange;
  }

  public handlePermissionRoleChange = (rowIndex: number, newValue: string): void => {
    if (this.isRowInEditMode(rowIndex)) {
      this.setState((prevState) => {
        return { editModeRowPermissions: this.getPermissionsWithPermissionRoleChange(prevState.editModeRowPermissions, newValue) };
      });
    } else {
      this.openEditMode(rowIndex, this.getPermissionsWithPermissionRoleChange(this.props.permissions.items[rowIndex].permissionsByContentType, newValue));
    }
  }

  private getRoleColumn = (): DataGrid.Column => {
    const rowToPermissionRoleData = (row: DataGrid.Row): PermissionsGridCells.IPermissionRole => {
      const permissionsData = row.data as PermissionsGridRow;
      const isProjectLeader = permissionsData.isProjectLeader || permissionsData.isInheritedProjectLeader;
      const inEditMode = this.isRowInEditMode(permissionsData.index);
      let permissionRole: PermissionRoleName;
      let permissionRoleOptions: PermissionRoleName[];

      if (inEditMode) {
        permissionRole = this.props.permissionRoleHelper.getExplicitRoleForTypeAndPermissions(
          this.props.authorizableType,
          { permissionsByContentType: this.state.editModeRowPermissions },
        );
        permissionRoleOptions = this.getPermissionRoleOptions(permissionRole);
      } else if (isProjectLeader) {
        permissionRole = PermissionRoleName.Administer;
        permissionRoleOptions = [PermissionRoleName.Administer];
      } else {
        permissionRole = permissionsData.permissionRole;
        permissionRoleOptions = permissionsData.permissionRoleOptions;
      }

      return {
        isProjectLeader: isProjectLeader,
        isUser: permissionsData.isUser,
        permissionRole,
        permissionRoleOptions,
        rowStatus: permissionsData.status,
      };
    };
    const rowToSiteRoleData = (row: DataGrid.Row): PermissionsGridCells.ISiteRole => {
      const permissionsData = row.data as PermissionsGridRow;
      return {
        siteRole: permissionsData.siteRole,
        rowStatus: permissionsData.status,
      };
    };

    const getHeaderText = (): string => {
      if (this.props.isExplicitPermissions) {
        return Messages.permissionsRulesRoleHeader();
      } else if (this.props.isSessionSiteOrServerAdmin) {
        return Messages.permissionsEffectiveRoleHeader();
      } else {
        return '';
      }
    };

    return {
      name: 'role',
      dataKey: this.props.isExplicitPermissions ? 'permissionRole' : 'siteRole',
      headerText: getHeaderText(),
      resizable: false,
      sortable: false,
      headerFormatter: PermissionsGridCells.RoleHeaderCell(this.isHeaderDisabled()),
      cellFormatter: this.props.isExplicitPermissions ?
        PermissionsGridCells.PermissionRoleCell(this.handlePermissionRoleChange, rowToPermissionRoleData, this.props.isEditable) :
        PermissionsGridCells.SiteRoleCell(rowToSiteRoleData),
    };
  }

  private toggleCapabilityValue = (capabilityName: string): void => {
    this.setState((prevState) => {
      let newEditModeRowPermissions: _.Dictionary<_.Dictionary<IPermission>> = deepClone(prevState.editModeRowPermissions);
      const capability: IPermission = prevState.editModeRowPermissions[this.props.authorizableType][capabilityName];
      // change Allowed to Denied, Denied to Unspecified, Unspecified/Mixed to Allowed.
      let permission: Permission;
      switch (capability.permission) {
        case Permission.Allowed:
          permission = Permission.Denied;
          break;
        case Permission.Denied:
          permission = Permission.Unspecified;
          break;
        case Permission.Unspecified:
        case Permission.Mixed:
        default:
          permission = Permission.Allowed;
      }
      newEditModeRowPermissions[this.props.authorizableType][capabilityName].permission = permission;
      return { editModeRowPermissions: newEditModeRowPermissions };
    });
  }

  public handleCapabilityCellClick = (capabilityName: string, rowIndex: number): void => {
    if (this.isRowInEditMode(rowIndex)) {
      this.toggleCapabilityValue(capabilityName);
    } else {
      this.openEditMode(rowIndex, this.props.permissions.items[rowIndex].permissionsByContentType);
    }
  }

  private getCapabilitiesColumn = (): DataGrid.Column => {
    const rowToCapabilitiesData = (row: DataGrid.Row): PermissionsGridCells.ICapabilities => {
      const permissionsData = row.data as PermissionsGridRow;
      let capabilities: _.Dictionary<IPermission>;
      const isInEditMode = this.isRowInEditMode(permissionsData.index);
      if (isInEditMode) {
        capabilities = this.state.editModeRowPermissions[this.props.authorizableType];
      } else if (this.props.isExplicitPermissions && (permissionsData.isProjectLeader || permissionsData.isInheritedProjectLeader)) {
        const reason = permissionsData.isUser ? PermissionReason.UserProjectLeaderAllow : PermissionReason.GroupProjectLeaderAllow;
        capabilities = PermissionsHelper.AllowedIPermissions(permissionsData.permissions, reason);
      } else {
        capabilities = permissionsData.permissions;
      }
      return {
        capabilities,
        capabilitiesSections: this.props.capabilitySections,
        isExplicit: this.props.isExplicitPermissions,
        isProjectLeader: (permissionsData.isProjectLeader || permissionsData.isInheritedProjectLeader) && !isInEditMode,
        rowStatus: permissionsData.status,
        editabilityDescription: this.props.editabilityDescription,
      };
    };

    return {
      name: 'capabilities',
      dataKey: 'permissions',
      growToFit: true,
      headerText: '',
      resizable: false,
      sortable: false,
      headerFormatter: PermissionsGridCells.CapabilitiesHeaderCell(this.props.capabilitySections, this.isHeaderDisabled()),
      cellFormatter: PermissionsGridCells.CapabilitiesCell(this.handleCapabilityCellClick, rowToCapabilitiesData, this.props.isEditable),
    };
  }

  public getPermissionRoleOptions = (currentPermissionRole: PermissionRoleName): PermissionRoleName[] => {
    // Custom has to be one of the options when the current permission role is Custom
    return currentPermissionRole === PermissionRoleName.Custom ?
      [PermissionRoleName.Custom].concat(this.props.permissionRoleHelper.getAvailableRolesForType(this.props.authorizableType)) :
      this.props.permissionRoleHelper.getAvailableRolesForType(this.props.authorizableType);
  }

  private checkRowStatus = (index: number): RowStatus => {
    if (this.shouldShowRelevantRows()) {
      // 1. First check if the grid is supposed to highlight relevant rules of a different grid,
      // because this should override the normal row status evaluation.
      return this.isRowRelevant(index) ? RowStatus.RELEVANT : RowStatus.DISABLED;
    }
    if (this.isRowInEditMode(index)) {
      // 2. Then check if the row is opened for editing. If so, there are three possible states
      // (CLEAN, DELETE, DIRTY) evaluated in the sequence below to ensure saving edits on this
      // row is disabled until a change is actually made.
      if (!this.isRowEdited(index)) {
        return RowStatus.EDIT_CLEAN;
      } else if (this.isRowToBeDeleted(index)) {
        return RowStatus.EDIT_DELETE;
      } else {
        return RowStatus.EDIT_DIRTY;
      }
    } else {
      // 3. When the row is not opened for editing, check if any other special conditions apply,
      // such as if the row should be disabled or be highlighted from selection. Return the
      // default status (INITIAL) when none of the special conditions is met.
      if (this.isRowDisabled(index)) {
        return RowStatus.DISABLED;
      } else if (this.isRowSelected(index)) {
        return RowStatus.SELECTED;
      } else {
        return RowStatus.INITIAL;
      }
    }
  }

  private isProjectLeader = (granteePermissions: IGranteePermissions, index: number): boolean => {
    // project leader is not in a template, so it does not get set to unset if it is missing from API response
    if (granteePermissions.permissionsByContentType[AuthorizableType.PROJECT]) {
      const projectLeader = this.isRowInEditMode(index) ? this.state.editModeRowPermissions[AuthorizableType.PROJECT][Capabilities.projectLeader] : granteePermissions.permissionsByContentType[AuthorizableType.PROJECT][Capabilities.projectLeader];
      if (projectLeader !== undefined) {
        switch (projectLeader.permission) {
          case Permission.Allowed:
            return true;
          case Permission.Denied:
            return false;
          case Permission.Unspecified:
          case Permission.Mixed:
            return undefined;
        }
      }
    }
  }

  private granteePermissionsToRow = (value: IGranteePermissions, index: number): DataGrid.Row => {
    let granteeName: string;
    let granteeId: string;
    let siteRole: string;
    let isUser: boolean;
    let granteeFullyQualifiedName: string;
    if (value.user) {
      isUser = true;
      granteeName = value.user.displayName;
      granteeId = value.user.id;
      siteRole = (value.user as ISiteUserInfo).siteRole || '';
      granteeFullyQualifiedName = Messages.permissionsGranteeFullyQualifiedName_user({
        DISPLAY_NAME: granteeName,
        DOMAIN_NAME: value.user.domainName,
        USER_NAME: value.user.username,
      });
    } else if (value.group) {
      isUser = false;
      granteeName = value.group.name;
      granteeId = value.group.id;
      granteeFullyQualifiedName = Messages.permissionsGranteeFullyQualifiedName_group({
        DOMAIN_NAME: value.group.domainName,
        GROUP_NAME: granteeName,
      });
    }

    // project leader is not in a template, so it does not get set to unset if it is missing from API response
    const isProjectLeader = this.isProjectLeader(value, index);

    let isInheritedProjectLeader = false;
    if (value.permissionsByContentType[AuthorizableType.PROJECT]) {
      const inheritedProjectLeader = value.permissionsByContentType[AuthorizableType.PROJECT][Capabilities.inheritedProjectLeader];
      isInheritedProjectLeader = inheritedProjectLeader && inheritedProjectLeader.permission === Permission.Allowed;
    }
    const permissionRole = this.props.permissionRoleHelper.getExplicitRoleForTypeAndPermissions(
      this.props.authorizableType,
      value,
    );
    const permissionRoleOptions = this.getPermissionRoleOptions(permissionRole);

    // This grid component has to have access to permissions across all authorizables since editing across multiple
    // authorizables is supported. Thus logic for displaying the correct caps is internal to this component.
    let rowData: PermissionsGridRow = {
      granteeFullyQualifiedName,
      granteeName,
      granteeId,
      isUser,
      isInheritedProjectLeader,
      isProjectLeader,
      index,
      permissionRole,
      permissionRoleOptions,
      permissions: value.permissionsByContentType[this.props.authorizableType],
      siteRole,
      status: this.checkRowStatus(index),
    };

    return {
      key: index.toString(),
      data: rowData,
    };
  }

  private handleAddNewPermissionClick = (): void => {
    this.props.clearSearchTextBoxText();

    // Open edit mode with unspecified permissions and the next index after indices of existing permissions
    const nextIndex = this.props.permissions ? this.props.permissions.items.length : 0;
    this.openEditMode(nextIndex, this.props.unspecifiedPermissions, true);
  }

  private setScrollable = (scrollable: DataGrid.IScrollable) => {
    this.dataGridScrollHandler = scrollable;
  }

  private generateCols = (): DataGrid.Column[] => {
    return [this.getGranteeColumn(), this.getRoleColumn(), this.getCapabilitiesColumn()];
  }

  private generateRows = (): DataGrid.Row[] => {
    let granteePermissions: IGranteePermissions[] = this.props.permissions ? this.props.permissions.items : [];
    if (this.isNewRowInEditMode()) {
      granteePermissions = [...granteePermissions, this.state.newRowGranteePermissions];
    }
    return granteePermissions.map(this.granteePermissionsToRow);
  }

  private generateContent = (): void => {
    this.cols = this.generateCols();
    this.rows = this.generateRows();
  }

  public render(): JSX.Element {
    this.generateContent();

    // when in edit mode, the buttom panel actually appears on the next row, so add 1 to current row
    // the actual first row is the header row, so add another 1 to the row index
    let buttonPanelIndex;
    if (this.state.editModeRowIndex !== undefined) {
      buttonPanelIndex = this.state.editModeRowIndex + 2;
    }

    let numRows = this.rows.length + 1; // plus 1 to account for header
    const lastRowInEditMode = this.state.editModeRowIndex === this.rows.length - 1;
    const searching = this.isNewRowInEditMode();
    let numAddedRows = 0;
    if (lastRowInEditMode) {
      numRows++;
      numAddedRows = 1;
    }
    if (searching) {
      numAddedRows = 2;
    }
    let permissionsGridContainerClassName = PermissionsGridStyles.PermissionsGridContainerBase;
    const inEditMode = this.state.editModeRowIndex !== undefined;
    const isExplicit = this.props.isExplicitPermissions;
    const disabled = this.props.disableNonEditingRows;
    let args: PermissionsGridStyles.DataGridContainerStyleArgs = {
      numRows,
      numAddedRows,
      lastRowInEditMode,
      inEditMode,
      buttonPanelIndex,
      searching,
      isExplicit,
      disabled
    };

    let addPermissionArgs = {
      inEditMode,
      numRows,
      numAddedRows,
      searching,
    };

    const gridClassName = PermissionsGridStyles.memoizedDataGridContainer(args);
    return (
      <div className={permissionsGridContainerClassName} data-tb-test-id={`${PERMISSIONS_CONTENT_TESTID}-${this.props.authorizableType}`}>
        <div className={gridClassName} >
          <DataGrid
            cols={this.cols}
            config={{ rowOuterHeight: PermissionsGridStyles.RowHeight, rememberLastScrollPos: true, }}
            headerHeight={PermissionsGridStyles.RowHeight}
            rowStore={this.getRowStore()}
            scrollableHandler={this.setScrollable}
            showVerticalGridLines={false}
            showHorizontalGridLines={true}
            showRowBanding={false}
            onCellClick={this.handleCellClick}
            onCellDoubleClick={this.handleCellDoubleClick}
            selectionModel={this.selectionHandler()}
            style={{ overflow: 'unset', padding: '0px', marginTop: `${CommonStyles.Sizes.LargeSpace}px`, zIndex: 'auto' }} // tfs 1036251
          />
        </div>
        <PermissionsGridEmptyState
          isDisabled={this.props.disableNonEditingRows}
          isEditable={this.props.isEditable}
          isEmpty={(!this.props.permissions?.items?.length)}
          isExplicitPermissions={this.props.isExplicitPermissions}
        />
        {this.props.isEditable &&
          <AddNewPermission containerStyle={addPermissionArgs} disabled={this.props.disableNonEditingRows} onClick={this.handleAddNewPermissionClick} />}
      </div>
    );
  }
}
